import { icons, type LucideIcon } from 'lucide-react';

export const DEFAULT_LUCIDE_ICON_NAME = 'database';

const LEGACY_ICON_ALIASES: Record<string, string> = {
  books: 'book',
  gear: 'cog',
};

function pascalToKebab(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

function kebabToPascal(name: string): string {
  return name
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

export const LUCIDE_ICON_NAMES: readonly string[] = Object.keys(icons)
  .map(pascalToKebab)
  .sort((left, right) => left.localeCompare(right));

const LUCIDE_ICON_NAME_SET = new Set(LUCIDE_ICON_NAMES);

export function normalizeLucideIconName(
  name: string | null | undefined,
): string {
  if (!name || name.trim() === '') {
    return DEFAULT_LUCIDE_ICON_NAME;
  }

  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();
  const legacy = LEGACY_ICON_ALIASES[lower];
  if (legacy) {
    return legacy;
  }

  if (LUCIDE_ICON_NAME_SET.has(lower)) {
    return lower;
  }

  const fromPascal = pascalToKebab(trimmed);
  if (LUCIDE_ICON_NAME_SET.has(fromPascal)) {
    return fromPascal;
  }

  const fromSnake = lower.replace(/_/g, '-');
  if (LUCIDE_ICON_NAME_SET.has(fromSnake)) {
    return fromSnake;
  }

  return DEFAULT_LUCIDE_ICON_NAME;
}

export function resolveLucideIcon(name: string | null | undefined): LucideIcon {
  const normalized = normalizeLucideIconName(name);
  const pascal = kebabToPascal(normalized);
  return icons[pascal as keyof typeof icons] ?? icons.Database;
}
